import React, { useEffect, useState } from 'react';
import { Button, CardTitle, Field, PseudoStates } from '@cordata/buffet';
import { FieldArray, useFormikContext } from 'formik';
import { Row, Col } from 'reactstrap';
import PropTypes from 'prop-types';
import { find } from 'lodash';

import {
  referenceAdminInstance as config,
  authorizedInstanceNoBase as FullUrl,
} from '~/utils';

const COLORS = [{
  color: 'primary',
  label: 'blue',
  inverse: true,
}, {
  color: 'secondary',
  label: 'orange',
  inverse: false,
}, {
  color: 'tertiary',
  label: 'gray',
  inverse: false,
}, {
  color: 'success',
  label: 'green',
  inverse: true,
}, {
  color: 'warning',
  label: 'yellow',
  inverse: true,
}, {
  color: 'danger',
  label: 'red',
  inverse: true,
}, {
  color: 'light',
  label: 'white',
  inverse: false,
}, {
  color: 'dark',
  label: 'black',
  inverse: true,
}];

const WIDGET_TYPES = [
  'NCR',
  // 'Component',
].map(label => ({
  label,
  value: label,
}));

const Fields = ({ index, conf, helpers }) => {
  const { setFieldValue, values } = useFormikContext();
  const [reports, setReports] = useState(null);
  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    if (!conf.isActive) {
      setIsHidden(true);
    }
  }, [conf.isActive]);

  useEffect(() => {
    if (conf.type) {
      setFieldValue(`config.${index}.widgetType`, conf.type.value);

      if (conf.type.value === 'NCR') {
        config
          .get('reference-report-configuration')
          .then(({ data: { data: r } }) => setReports(r));
      }
    } else {
      setFieldValue(`config.${index}.report`, null);
      setReports(null);
    }
  }, [conf.type]);

  useEffect(() => {
    if (conf.sourceUid) {
      setFieldValue(`config.${index}.report`, find(reports, ['id', conf.sourceUid]));
    }
  }, [conf.sourceUid, reports]);

  useEffect(() => {
    if (conf.color) {
      setFieldValue(`config.${index}.inverse`, conf.color.inverse);
    }
  }, [conf.color]);

  useEffect(() => {
    if (conf.width) {
      setFieldValue(`config.${index}.dimension`, `${conf.width}:y`);
    }
  }, [conf.width]);

  useEffect(() => {
    if (conf.report) {
      setFieldValue(`config.${index}.sourceUid`, conf.report.id);
      setFieldValue(`config.${index}.widgetContainerConfigName`, conf.report.attributes.reportConfigurationName);
      setFieldValue(`config.${index}.widgetContainerConfigLabel`, conf.report.attributes.reportConfigurationLabel);
    }
  }, [conf.report]);

  return (
    <div className={isHidden && 'd-none'}>
      <Button
        size="sm"
        outline
        color="danger"
        spear
        type="button"
        onClick={() => {
          if (conf.id) {
            setFieldValue(`config.${index}.isActive`, false);
            setIsHidden(true);
          } else {
            helpers.remove(index);
          }
        }}
        icon="minus"
      >
        Remove config
      </Button>

      <Field
        name={`config.${index}.type`}
        label="Type"
        type="select"
        options={WIDGET_TYPES}
        required
      />

      {reports && !!reports.length &&
        <Field
          name={`config.${index}.report`}
          label="Report"
          type="select"
          options={reports}
          required={reports && reports.length}
          getOptionValue={o => o.id}
          getOptionLabel={o => o.attributes.reportConfigurationLabel}
        />
      }

      <Row form>
        <Col sm>
          <Field
            name={`config.${index}.sortOrder`}
            label="Sort Order"
            type="number"
            required
          />
        </Col>

        <Col sm>
          <Field
            name={`config.${index}.width`}
            label="Width"
            type="number"
            text="a number between 1 ~ 12"
            min={1}
            max={12}
          />
        </Col>
      </Row>

      <Row form>
        <Col sm>
          <Field
            name={`config.${index}.refreshInterval`}
            label="Refresh Interval"
            type="number"
            text="refresh widget ( in ms )"
          />
        </Col>

        <Col sm>
          <Field
            name={`config.${index}.color`}
            label="Color"
            type="select"
            options={COLORS}
            getOptionValue={o => o.color}
            required
          />
        </Col>
      </Row>

      {!isHidden && index !== values.config.filter(c => c.isActive).length - 1 && <hr />}
    </div>
  );
};

Fields.propTypes = {
  index: PropTypes.number.isRequired,
  helpers: PropTypes.shape().isRequired,
  conf: PropTypes.shape().isRequired,
};

const WidgetContainers = () => {
  const { values, setFieldValue } = useFormikContext();
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    setStatus('loading');

    if (values.relationships) {
      FullUrl
        .get(values.relationships.referenceWidgetContainerConfigs.related)
        .then(({ data: { data: wcc } }) => {
          setFieldValue('wcc', wcc);

          if (wcc.length && (!values.config || values.config.length !== 0)) {
            setFieldValue('config', wcc
              .map((wccfr) => {
                const options = wccfr.attributes.optionsJson
                  ? JSON.parse(wccfr.attributes.optionsJson)
                  : {};

                /* eslint-disable-next-line no-param-reassign */
                wccfr.attributes.optionsJson = options;

                return wccfr;
              })
              .sort((a, b) =>
                a.attributes.optionsJson.sortOrder - b.attributes.optionsJson.sortOrder)
              .map((wccfr) => {
                const o = wccfr.attributes.optionsJson;

                return {
                  ...o,
                  id: wccfr.id,
                  isActive: wccfr.attributes.isActive,
                  sourceUid: wccfr.attributes.sourceUid,
                  widgetContainerUid: wccfr.attributes.widgetContainerUid,
                  color: find(COLORS, ['color', o.color]),
                  type: find(WIDGET_TYPES, ['value', wccfr.attributes.widgetType]),
                  width: o.dimension && o.dimension.split(':')[0],
                };
              }));

            setStatus('success');
          } else {
            setStatus('success');
          }
        })
        .catch((e) => {
          setStatus('error');
          console.error(e);
        });
    } else {
      setFieldValue('wcc', []);
      setFieldValue('config', []);
      setStatus('success');
    }
  }, [values.relationships]);

  return (
    <>
      <CardTitle>Widget Config</CardTitle>

      {status === 'success' ?
        <FieldArray name="config">
          {helpers => (
            <React.Fragment>
              {values.config && values.config.map((v, i) => (
                <React.Fragment key={v && v.id ? v.id : `new-config-${i}`}>
                  <Fields index={i} conf={v} helpers={helpers} />
                </React.Fragment>
              ))}

              <Button
                size="sm"
                outline
                color="tertiary"
                spear
                type="button"
                onClick={() => helpers.push({ isActive: true })}
                icon="plus"
              >
                Add config
              </Button>
            </React.Fragment>
          )}
        </FieldArray>
        : <PseudoStates status={status} />
      }
    </>
  );
};

export default WidgetContainers;
